/* =========================================================
   App shell — auth gate, chrome, routing.

   Outside the update window every non-ETC-Admin role is
   read-only; the banner says so and the edit affordances on
   each screen check `readOnly`.
   ========================================================= */
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Banner, BottomNav, Sidebar, ToastHost, Topbar } from '@/components/ui';
import { apiSource, ds, userOrgIds } from '@/data';
import { onWriteFailure } from '@/data/ApiDataSource';
import { onSessionExpired } from '@/lib/api';
import { restoreSession } from '@/lib/session';
import { onLangChange, useI18n } from '@/i18n';
import { completeness, siteSchema } from '@/lib/fields';
import { useIsMobile } from '@/hooks/useIsMobile';
import { INACTIVIDAD_MIN, useInactividad } from '@/hooks/useInactividad';
import { PATHS, routeFor } from '@/hooks/useRoute';
import { useAppStore } from '@/store/useAppStore';
import { LoadingScreen } from '@/screens/auth/LoadingScreen';
import { LoginScreen } from '@/screens/auth/LoginScreen';
import { ForcePasswordChange } from '@/screens/auth/ForcePasswordChange';
import { Dashboard } from '@/screens/dashboard/Dashboard';
import { OrgScreen } from '@/screens/org/OrgScreen';
import { SiteScreen } from '@/screens/sites/SiteScreen';
import { InstructorScreen } from '@/screens/instructors/InstructorScreen';
import { AdminScreen } from '@/screens/admin/AdminScreen';
import { UsersScreen } from '@/screens/users/UsersScreen';
import { ReportsScreen } from '@/screens/reports/ReportsScreen';
import { esDeUnSite } from '@/lib/roles';

/** Is everything in the user's scope complete? Hides the orange reminder. */
function useScopeComplete(): boolean {
  const role = useAppStore((s) => s.role);
  const user = useAppStore((s) => s.user);
  if (!user) return false;
  try {
    let sites = ds.listSites();
    if (role === 'PARTNER_MGR') {
      const oids = userOrgIds(user);
      sites = sites.filter((s) => oids.includes(s.OrgId));
    } else if (esDeUnSite(role) && user.siteId) {
      sites = sites.filter((s) => s.SiteId === user.siteId);
    }
    if (!sites.length) return false;
    const pending = sites.filter(
      (s) => completeness(siteSchema, s).missing > 0 || s.SitePostalCode === '0000',
    );
    return pending.length === 0;
  } catch {
    return false;
  }
}

/** Blocks a route the active role may not open. */
function AdminOnly({ children }: { children: React.ReactNode }) {
  const { lang } = useI18n();
  const role = useAppStore((s) => s.role);
  if (role !== 'ETC_ADMIN') {
    return (
      <div className="card" style={{ padding: 24, textAlign: 'center' }}>
        {lang === 'es' ? 'Sin acceso.' : 'No access.'}
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const user = useAppStore((s) => s.user);
  const loggingIn = useAppStore((s) => s.loggingIn);
  const loggingOut = useAppStore((s) => s.loggingOut);
  const windowState = useAppStore((s) => s.windowState);
  const beginLogin = useAppStore((s) => s.beginLogin);
  const beginLogout = useAppStore((s) => s.beginLogout);
  const setScreen = useAppStore((s) => s.setScreen);
  const setStoreLang = useAppStore((s) => s.setLang);

  const scopeComplete = useScopeComplete();
  const addToast = useAppStore((s) => s.addToast);
  const dropSession = useAppStore((s) => s.dropSession);
  const adoptSession = useAppStore((s) => s.adoptSession);
  const retractSuccessToasts = useAppStore((s) => s.retractSuccessToasts);

  // Una cuenta recién dada de alta entra con la contraseña temporal del
  // CSV: hasta que ponga la suya no ve la aplicación. No se guarda en el
  // navegador a propósito — si recarga, el servidor lo vuelve a decir.
  const [mustChangePw, setMustChangePw] = useState(false);

  // Por qué se volvió a la pantalla de acceso, cuando no fue por decisión
  // propia. Se limpia al entrar de nuevo.
  const [avisoAcceso, setAvisoAcceso] = useState('');

  // Al recargar, el usuario sigue guardado pero el access token no: vivía en
  // memoria. Se recupera con la cookie de refresco y se vuelve a hidratar
  // antes de pintar nada, para que ninguna pantalla salga vacía.
  const [restoring, setRestoring] = useState(() => !!user && !apiSource().hydrated);
  useEffect(() => {
    if (!restoring) return;
    let alive = true;
    void restoreSession().then((res) => {
      if (!alive) return;
      if (res === null) dropSession();
      // el servidor manda: su rol y su alcance sustituyen a lo guardado
      else if (res !== 'sin-cambios') adoptSession(res);
      setRestoring(false);
    });
    return () => {
      alive = false;
    };
  }, [restoring, dropSession, adoptSession]);
  const setWindowState = useAppStore((s) => s.setWindowState);

  // con backend real: un guardado rechazado se revierte y se explica, y una
  // sesión caducada devuelve al acceso en vez de dejar la pantalla muerta
  // el idioma sigue a la cuenta, no al navegador; si el guardado falla, la
  // pantalla ya cambió y lo único que se pierde es que la próxima sesión
  // desde otro equipo salga en el idioma anterior
  useEffect(() => {
    onLangChange((l) => void ds.setLang(l).catch(() => {}));
  }, []);

  useEffect(() => {
    onWriteFailure((message) => {
      // el guardado optimista ya había cantado victoria: se retira antes
      // de contar el fallo, para no dejar dos avisos que se contradicen
      retractSuccessToasts();
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se guardó' : 'Not saved',
        body: message,
        details: lang === 'es'
          ? 'La pantalla puede seguir mostrando lo que escribiste. Recarga la página para ver el valor real.'
          : 'The screen may still show what you typed. Reload the page to see the real value.',
      });
    });
    onSessionExpired(() => {
      setAvisoAcceso(lang === 'es'
        ? 'Tu sesión caducó. Vuelve a entrar.'
        : 'Your session expired. Please sign in again.');
      beginLogout(() => navigate(PATHS.login, { replace: true }));
    });
  }, [addToast, beginLogout, lang, navigate, retractSuccessToasts]);

  // Una pestaña olvidada en un equipo compartido no debe quedarse abierta.
  // El cierre revoca también la cookie de refresco en el servidor, porque
  // beginLogout pasa por endSession().
  useInactividad(!!user && !loggingOut, () => {
    setAvisoAcceso(lang === 'es'
      ? `Se cerró la sesión tras ${INACTIVIDAD_MIN} minutos sin actividad.`
      : `Signed out after ${INACTIVIDAD_MIN} minutes of inactivity.`);
    beginLogout(() => navigate(PATHS.login, { replace: true }));
  });

  // la ventana de actualización la manda el servidor, no el navegador
  useEffect(() => {
    const src = apiSource();
    if (user && src?.hydrated) {
      setWindowState({ open: src.window.open, deadline: src.window.deadline ?? '' });
    }
  }, [user, setWindowState]);

  // keep the store's copies of screen + language in sync for non-React callers
  const route = routeFor(location.pathname, location.search);
  useEffect(() => setScreen(route.screen), [route.screen, setScreen]);
  useEffect(() => setStoreLang(lang), [lang, setStoreLang]);

  if (restoring) return <LoadingScreen lang={lang} duration={1200} mode="in" />;
  if (loggingIn) return <LoadingScreen lang={lang} duration={4000} mode="in" />;
  if (loggingOut) return <LoadingScreen lang={lang} duration={4000} mode="out" />;

  if (!user) {
    return (
      <LoginScreen
        aviso={avisoAcceso}
        onLogin={(u, mustChange) => {
          setAvisoAcceso('');
          setMustChangePw(Boolean(mustChange));
          beginLogin(u, () => navigate(PATHS.dashboard, { replace: true }));
        }}
      />
    );
  }

  if (mustChangePw) {
    return (
      <ForcePasswordChange
        name={user.name}
        email={user.username}
        onDone={() => setMustChangePw(false)}
        onCancel={() => {
          setMustChangePw(false);
          beginLogout(() => navigate(PATHS.login, { replace: true }));
        }}
      />
    );
  }

  // signed in: /login is not a place to stay
  if (location.pathname === PATHS.login) return <Navigate to={PATHS.dashboard} replace />;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <Topbar onLogout={() => beginLogout(() => navigate(PATHS.login, { replace: true }))} />
      <Banner open={windowState.open} deadline={windowState.deadline} hidden={scopeComplete} />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {!isMobile ? <Sidebar /> : null}
        <main style={{ flex: 1, padding: isMobile ? '12px 12px 80px' : 22, overflowX: 'hidden', minWidth: 0 }}>
          <Routes>
            <Route path={PATHS.dashboard} element={<Dashboard />} />
            <Route path={`${PATHS.org}/:orgId?`} element={<OrgScreen />} />
            <Route path={`${PATHS.sites}/:siteId?`} element={<SiteScreen />} />
            <Route path={`${PATHS.instructors}/:instructorId?`} element={<InstructorScreen />} />
            <Route path={PATHS.users} element={<AdminOnly><UsersScreen /></AdminOnly>} />
            <Route path={PATHS.reports} element={<AdminOnly><ReportsScreen /></AdminOnly>} />
            <Route path={PATHS.admin} element={<AdminOnly><AdminScreen /></AdminOnly>} />
            <Route path="*" element={<Navigate to={PATHS.dashboard} replace />} />
          </Routes>
        </main>
      </div>
      {isMobile ? <BottomNav /> : null}
      <ToastHost />
    </div>
  );
}
